import {
	createORPCClient,
	type InferClientInputs,
	type InferClientOutputs,
	isDefinedError,
} from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ErrorFromErrorMap, InferContractRouterErrorMap, ORPCError } from "@orpc/contract";
import type { RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import * as Sentry from "@sentry/tanstackstart-react";
import { createIsomorphicFn } from "@tanstack/react-start";
import { serverORPCClient } from "@/orpc/client.server";
import type router from "@/orpc/router";

export function isORPCClientError(error: unknown): error is ORPCError<any, any> {
	return isDefinedError(error);
}

/**
 * Safely stringify an object, handling circular references and other serialization issues
 */
function safeJsonStringify(obj: unknown): string {
	try {
		const seenObjects = new WeakSet();
		return JSON.stringify(obj, (_key, value) => {
			// Handle circular references by replacing them with a placeholder
			if (typeof value === "object" && value !== null) {
				if (seenObjects.has(value)) {
					return "[Circular Reference]";
				}
				seenObjects.add(value);
			}

			// Handle functions, symbols, and other non-serializable types
			if (typeof value === "function") {
				return "[Function]";
			}
			if (typeof value === "symbol") {
				return "[Symbol]";
			}
			if (typeof value === "undefined") {
				return "[undefined]";
			}

			return value;
		});
	} catch (error) {
		// If all else fails, return a safe fallback
		return `[Serialization Error: ${error instanceof Error ? error.message : "Unknown"}]`;
	}
}

export function shouldCaptureORPCClientError(error: unknown): boolean {
	if (!isORPCClientError(error)) {
		return true;
	}

	return typeof error.status !== "number" || error.status >= 500;
}

const getORPCClient = createIsomorphicFn()
	.server(() => serverORPCClient)
	.client((): RouterClient<typeof router> => {
		const link = new RPCLink({
			url: `${window.location.origin}/api/rpc`,
			interceptors: [
				async (options) => {
					const response = await options.next();

					return response;
				},
				async function sentryInterceptor({ next, path, input }) {
					let originalError: unknown;

					try {
						return await Sentry.startSpan(
							{
								name: `orpc.${path.join("/")}`,
								attributes: {
									"span.kind": "CLIENT",
									"rpc.system": "orpc",
									"rpc.method": path.join("."),
									"rpc.arguments": input ? safeJsonStringify(input) : undefined,
								},
							},
							async (span) => {
								try {
									const result = await next();
									return result;
								} catch (error) {
									originalError = error;

									try {
										span.setStatus({
											code: 2,
										});
										if (shouldCaptureORPCClientError(error)) {
											Sentry.captureException(error);
										}
									} catch (sentryError) {
										// Log Sentry errors but don't let them interfere with the original error
										// eslint-disable-next-line no-console
										console.warn("Sentry span instrumentation failed:", sentryError);
									}

									// Re-throw the original error
									throw error;
								}
							},
						);
					} catch (sentryError) {
						if (sentryError === originalError) {
							throw sentryError;
						}

						// If Sentry itself fails, log the error and continue with the original call
						// eslint-disable-next-line no-console
						console.error("Sentry interceptor failed:", sentryError);
						return await next();
					}
				},
			],
		});

		return createORPCClient(link);
	});

export const client: RouterClient<typeof router> = getORPCClient();

export const orpc = createTanstackQueryUtils(client);

export type Inputs = InferClientInputs<typeof client>;
export type Outputs = InferClientOutputs<typeof client>;

export type ValidationErrorResult = {
	fields?: Record<string, string[] | undefined>;
	form: string[];
};

export function validationErrors(e: unknown): ValidationErrorResult | undefined {
	const error = e as ErrorFromErrorMap<InferContractRouterErrorMap<typeof router>>;

	if (isDefinedError(error)) {
		if (error.code === "INPUT_VALIDATION_FAILED") {
			return {
				fields: error.data.fieldErrors,
				form:
					error.data.formErrors.length > 0
						? error.data.formErrors
						: ["Please verify the form and try again."],
			};
		}
		if (error.message) {
			return { form: [error.message] };
		}
	}
}
